import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { useCollection } from "../hooks/useCollection";
import { useDoc } from "../hooks/useDoc";
import { useCallableAction } from "../hooks/useCallableAction";
import { useToast } from "../components/Toast";
import { StatusBadge } from "../components/StatusBadge";
import { LoadingBlock, Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { DataTable } from "../components/DataTable";
import { date, dt, n } from "../lib/format";
import {
  cuttingLineItemId,
  cuttingLineProductName,
  productionCustomer,
  productionSourceTitle,
  type CuttingLineFields,
} from "../lib/productionFields";

import { ShortageDialog, extractShortages, statusOrder, useDetailedCallable, type ProductionDoc, type ProductionStatusResult, type ShortageState, type StatusDoc } from "./Production";

/**
 * Tek üretim kaydı — eski sistemde KARŞILIĞI YOKTU.
 *
 * Eski `production.php` sadece kart listesiydi: tek kaydın hangi kalemleri
 * içerdiği, ne tüketeceği, kesim ölçülerinin ne olduğu hiçbir ekranda yoktu.
 * Atölye tabletinde çalışan üretim personelinin ana ekranı burasıdır.
 *
 * · "Stok yeterli mi?" → `previewStockConsumption`: tamamlamadan ÖNCE eksik
 *   bileşenleri gösterir. Eski sistemde eksik malzeme ancak tamamladıktan sonra,
 *   o da sessizce atlanarak ortaya çıkıyordu.
 * · Statü değişimi `setProductionStatus` callable'ı ile yapılır; `prod-completed`
 *   stok düşümünü transaction içinde ve idempotency anahtarıyla tetikler.
 * · Maliyet/kâr gösterilmez.
 */

const CALLABLE_SET_STATUS = "setProductionStatus";
const CALLABLE_PREVIEW = "previewStockConsumption";

/** Security Rules'ta sipariş kalemleri `orders/{id}/items` altındadır. */
/** Sipariş kalemleri. Shopify terminolojisi; indeksler de bu adla tanımlı. */
const ITEMS_SUBCOLLECTION = "lineItems";

interface ItemDoc {
  title?: string;
  productName?: string;
  variantTitle?: string;
  optionSize?: string;
  sku?: string;
  quantity?: number;
  imageUrl?: string;
  productId?: string;
  isProductDeleted?: boolean;
}

/**
 * ★ ALAN ADLARI ÜÇ DİLDE. Bkz. `lib/productionFields.ts → cuttingLineItemId`.
 * Bu ekran yalnızca `orderItemId` + `productName` okuyordu; sunucu
 * `lineItemId` + `productTitle`, göç betiği ise `itemId` yazmıştı. Sonuç:
 * kesim tablosunun "Ürün" sütunu her satırda "—" görünüyordu.
 */
interface CuttingLineDoc extends CuttingLineFields {
  size1?: number;
  size2?: number;
  quantity?: number;
  thickness?: number;
  originOfMaterial?: string;
}

/** İki kaynağın (gömülü özet / sipariş kalemleri) indirgendiği tek satır biçimi. */
interface DetailItem {
  key: string;
  name: string;
  detail?: string;
  quantity?: number;
  imageUrl?: string;
  productId?: string;
  sku?: string;
  isProductDeleted?: boolean;
}

interface PlanRow {
  componentId: string;
  name: string;
  unit: string;
  needed: number;
  available: number;
}

interface PreviewResult {
  /** ★ Sunucudaki ad `lines`; `plan` diye bir alan dönmüyordu. */
  lines?: PlanRow[];
  shortages?: Array<{ componentId: string; missing: number }>;
  alreadyProcessed?: boolean;
}

const ORIGIN_LABEL: Record<string, string> = {
  plaka: "Plakadan",
  stok: "Stoktan",
  parca: "Parçadan",
};

export function ProductionDetail() {
  const { productionId = "" } = useParams();
  const { isStaff } = useCan();
  const toast = useToast();

  const [targetStatus, setTargetStatus] = useState("");
  const [shortage, setShortage] = useState<ShortageState | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const prodQ = useDoc<ProductionDoc>(
    () => (productionId ? doc(db, "production", productionId) : null),
    [productionId],
  );
  const production = prodQ.data;

  const statusesQ = useCollection<StatusDoc>(
    () => query(collection(db, "statuses"), where("category", "==", "production")),
    [],
  );
  const statuses = useMemo(
    () =>
      statusesQ.docs
        .filter((s) => s.obsolete !== true)
        .sort((a, b) => statusOrder(a) - statusOrder(b)),
    [statusesQ.docs],
  );
  const currentStatus = statuses.find((s) => s.id === production?.status);

  const cuttingQ = useCollection<CuttingLineDoc>(
    () => (productionId ? collection(db, "production", productionId, "cuttingList") : null),
    [productionId],
  );

  const orderId = production?.sourceType === "order" ? (production.sourceId ?? "") : "";
  const itemsQ = useCollection<ItemDoc>(
    () => (orderId ? collection(db, "orders", orderId, ITEMS_SUBCOLLECTION) : null),
    [orderId],
  );

  /**
   * ★ KALEM LİSTESİ İKİ KAYNAKTAN GELİR.
   *
   * Bu ekran eskiden YALNIZCA `orders/{id}/lineItems` okuyordu ve
   * `sourceType !== "order"` olan her kayıtta "Bu kayıt siparişten doğmadı;
   * kalem listesi yoktur." basıyordu — `itemSummary` dolu olsa bile. SSH
   * kaydında üretilecek ürünün adı, adedi ve görseli bu ekranda HİÇ
   * görünmüyordu. Artık özet varsa o kullanılır (ek okuma da yapılmaz).
   */
  const summary = useMemo(() => production?.itemSummary ?? [], [production?.itemSummary]);

  const detailItems = useMemo<DetailItem[]>(() => {
    if (summary.length > 0) {
      return summary.map((it, i) => ({
        key: `s-${i}`,
        name: it.title ?? it.productName ?? "Ürün",
        detail: it.variantTitle,
        quantity: it.quantity,
        imageUrl: it.imageUrl,
        productId: it.productId,
      }));
    }
    return itemsQ.docs.map((it) => ({
      key: it.id,
      name: it.title ?? it.productName ?? "Ürün",
      detail: it.optionSize,
      quantity: it.quantity,
      imageUrl: it.imageUrl,
      productId: it.productId,
      sku: it.sku,
      isProductDeleted: it.isProductDeleted,
    }));
  }, [summary, itemsQ.docs]);

  /**
   * Eksik görseller ürün kartından TEK SEFERLİK tamamlanır (dinleyici açılmaz).
   * Göçten gelen SSH kayıtlarında görsel alanı hiç yok; onarım betiği yazana
   * kadar da atölye görseli görsün.
   */
  const [productImages, setProductImages] = useState<Record<string, string>>({});
  const fetchedRef = useRef<Set<string>>(new Set());
  const missingImageKey = useMemo(
    () =>
      [...new Set(detailItems.filter((i) => !i.imageUrl && i.productId).map((i) => i.productId!))]
        .sort()
        .join(","),
    [detailItems],
  );

  useEffect(() => {
    let cancelled = false;
    const ids = (missingImageKey ? missingImageKey.split(",") : []).filter(
      (id) => !fetchedRef.current.has(id),
    );
    if (ids.length === 0) return;
    ids.forEach((id) => fetchedRef.current.add(id));

    void Promise.all(
      ids.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "products", id));
          const url = snap.exists() ? (snap.data() as { imageUrl?: unknown }).imageUrl : undefined;
          return [id, typeof url === "string" ? url : ""] as const;
        } catch (e) {
          console.error("Ürün görseli okunamadı", id, e);
          return [id, ""] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setProductImages((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      cancelled = true;
    };
  }, [missingImageKey]);

  const setStatus = useDetailedCallable<
    { productionIds: string[]; status: string; allowNegative?: boolean; reason?: string },
    ProductionStatusResult
  >(CALLABLE_SET_STATUS);

  const previewAction = useCallableAction<{ productionId: string }, PreviewResult>(
    CALLABLE_PREVIEW,
  );

  useEffect(() => {
    if (production?.status) setTargetStatus(production.status);
  }, [production?.status]);

  const runPreview = async () => {
    const out = await previewAction.run({ productionId });
    if (!out) {
      toast.error(previewAction.error ?? "Stok önizlemesi alınamadı.");
      return;
    }
    setPreview(out);
    const missing = out.shortages ?? [];
    if (missing.length > 0) {
      toast.error(
        `${missing.length} bileşende stok yetersiz.`,
        "Üretimi tamamlamadan önce stok girişi yapın.",
      );
    } else if (out.alreadyProcessed) {
      toast.info("Bu üretim için stok zaten düşülmüş. Tekrar düşülmeyecek.");
    } else {
      toast.success("Tüm bileşenler için stok yeterli.");
    }
  };

  const runStatusChange = async (allowNegative: boolean, reason?: string) => {
    if (!targetStatus) return;

    const res = await setStatus.run({
      productionIds: [productionId],
      status: targetStatus,
      ...(allowNegative ? { allowNegative: true, reason } : {}),
    });

    if (!res.ok) {
      if (res.shortages.length > 0) {
        setShortage({ message: res.message, shortages: res.shortages, retryIds: [productionId] });
        return;
      }
      toast.error(res.message);
      return;
    }

    const row = res.out.results?.[0];
    const list = row ? extractShortages(row) : [];
    if (list.length > 0) {
      setShortage({
        message:
          row?.error ??
          "Üretim tamamlanamadı: bileşen stoğu yetersiz. Hiçbir stok hareketi yazılmadı.",
        shortages: list,
        retryIds: [productionId],
      });
      return;
    }
    if (row && !row.ok) {
      toast.error(row.error ?? "Statü güncellenemedi.");
      return;
    }

    setShortage(null);
    if (row?.skipped) toast.info("Kayıt zaten bu statüdeydi; değişiklik yapılmadı.");
    else if (row?.alreadyProcessed)
      toast.success("Statü güncellendi.", "Stok daha önce düşülmüştü, tekrar düşülmedi.");
    else if (row?.forced)
      toast.success("Üretim tamamlandı.", "Stok negatife düştü; gerekçe kaydedildi.");
    else toast.success("Statü güncellendi.");
  };

  if (prodQ.loading) return <LoadingBlock label="Üretim kaydı yükleniyor" />;

  if (prodQ.error) {
    return (
      <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
        {prodQ.error}
      </div>
    );
  }

  if (!production) {
    return (
      <EmptyState
        title="Üretim kaydı bulunamadı"
        description={`Kayıt: ${productionId}`}
        tone="warning"
        action={
          <Link className="btn-secondary" to="/production">
            Üretim listesine dön
          </Link>
        }
      />
    );
  }

  const created = production.createdAt ?? production.intime ?? null;
  // Gömülü özet varsa sipariş kalemleri hiç okunmaz; "yükleniyor" da yalnızca
  // gerçekten okuma yapılan hâlde gösterilir (SSH kaydında sonsuz spinner olmaz).
  const itemsLoading = summary.length === 0 && orderId !== "" && itemsQ.loading;

  /** Not kutusunun başlığı kaynağa göre değişir; SSH'ta "not" değil, İŞ TARİFİDİR. */
  const workLabel =
    production.sourceType === "ssh"
      ? "Üretilecek iş (SSH)"
      : production.sourceType === "stockOrder"
        ? "Üretilecek iş (stok siparişi)"
        : "Üretim notu";
  // ★ SSH ve stok siparişinde `sourceLabel` HİÇ okunmuyordu: sunucu
  // "SSH #61046" yazsa bile başlıkta "SSH-{doküman kimliği}" görünüyordu.
  const sourceTitle = productionSourceTitle(production);

  const busy = setStatus.pending;

  return (
    <div className="space-y-5">
      {/* Başlık */}
      <div>
        <nav aria-label="Sayfa yolu" className="text-sm">
          <ol role="list" className="flex items-center gap-2 text-gray-500">
            <li>
              <Link to="/production" className="hover:text-gray-700">
                Üretim
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">{sourceTitle}</li>
          </ol>
        </nav>

        <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            {sourceTitle}
          </h1>

          <div className="flex flex-wrap items-center gap-2 noprint">
            <label htmlFor="detail-status" className="sr-only">
              Yeni statü
            </label>
            <select
              id="detail-status"
              className="select w-auto"
              value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value)}
              disabled={busy}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label ?? s.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-danger"
              disabled={busy || !targetStatus || targetStatus === production.status}
              onClick={() => void runStatusChange(false)}
            >
              Statüyü Değiştir
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={previewAction.pending}
              onClick={() => void runPreview()}
            >
              {previewAction.pending ? "Kontrol ediliyor…" : "Stok yeterli mi?"}
            </button>
            <button type="button" className="btn-secondary" onClick={() => window.print()}>
              Yazdır
            </button>
            {busy ? <Spinner size="sm" label="İşleniyor" /> : null}
          </div>
        </div>
      </div>

      {/* Özet kart */}
      <section className="card card-pad">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge
            technicName={production.status}
            label={currentStatus?.label ?? production.status}
            color={currentStatus?.color}
          />
          {production.priority === "urgent" || production.priority === "Acil" ? (
            <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
              Acil
            </span>
          ) : null}
          {production.stockReductionDone ? (
            <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              Stok düşüldü
            </span>
          ) : null}
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Kaynak</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {production.sourceType === "order" && production.sourceId ? (
                <Link className="link" to={`/orders/${encodeURIComponent(production.sourceId)}`}>
                  {sourceTitle}
                </Link>
              ) : (
                sourceTitle
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Üretime giriş
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{dt(created)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Müşteri</dt>
            {/* ★ `??` BOŞ METNİ yakalamaz: `customerShortName: ""` yazılmış bir
                kayıtta yedek alana hiç düşmüyor, ekranda boşluk kalıyordu.
                Sipariş numarası da burada gösterilir — atölye hangi siparişin
                işini yaptığını görmek için detaya inmek zorunda kalmasın. */}
            <dd className="mt-1 text-sm text-gray-900">
              {/* Liste ekranıyla AYNI sıra: atölye tam adı görür. Liste "Ezgi Ataş"
                  derken detay "Ezgi" diyordu. */}
              {productionCustomer(production) || "—"}
            </dd>
            {production.orderName ? (
              <dd className="mt-0.5 text-xs text-gray-600 tabular-nums">{production.orderName}</dd>
            ) : null}
          </div>
        </dl>

        {/* ★ İŞİN TARİFİ EN ÜSTTE VE BÜYÜK.
            Eskiden bu blok yalnızca küçük bir "Üretim notu:" satırıydı ve SSH
            kayıtlarında zaten hep boştu (canlıda 138 SSH üretim kaydının
            134'ünde `note` alanı HİÇ YOKTU). Atölye ne üreteceğini hiçbir
            ekranda göremiyordu. Sunucu artık SSH'ın arıza açıklamasını buraya
            taşıyor; başlık kaynağa göre değişir. */}
        {production.note ? (
          <div className="mt-4 rounded-md border-l-4 border-amber-400 bg-amber-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
              {workLabel}
            </p>
            <p className="mt-1 whitespace-pre-line text-base text-amber-900">{production.note}</p>
          </div>
        ) : production.sourceType !== "order" ? (
          <p className="mt-4 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
            Bu kayıt için iş tarifi girilmemiş. SSH kaydı üretime yeniden gönderilirse açıklama
            buraya taşınır.
          </p>
        ) : null}
      </section>

      {/* Stok önizlemesi */}
      {preview ? (
        <section className="card">
          <div className="card-pad border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Tüketilecek bileşenler
              {preview.alreadyProcessed ? " — stok zaten düşülmüş" : ""}
            </h2>
          </div>
          {(preview.lines ?? []).length === 0 ? (
            <p className="card-pad text-sm text-amber-900">
              Reçete bulunamadı; bu üretim hiçbir bileşen tüketmiyor. Reçetesiz ürün sessizce
              atlanmaz, uyarı kaydı üretilir.
            </p>
          ) : (
            <DataTable<PlanRow>
              caption="Üretimin tüketeceği bileşenler ve mevcut stok"
              rows={preview.lines ?? []}
              rowKey={(r) => r.componentId}
              columns={[
                { key: "name", header: "Bileşen", render: (r) => r.name },
                {
                  key: "needed",
                  header: "Gereken",
                  numeric: true,
                  render: (r) => n(r.needed, r.unit ? ` ${r.unit}` : ""),
                },
                {
                  key: "available",
                  header: "Mevcut",
                  numeric: true,
                  render: (r) => (
                    <span className={r.available < r.needed ? "font-semibold text-red-700" : ""}>
                      {n(r.available)}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </section>
      ) : null}

      {/* Kalemler */}
      <section className="card">
        <div className="card-pad border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Üretilecek kalemler</h2>
        </div>

        {itemsLoading ? (
          <LoadingBlock label="Kalemler yükleniyor" />
        ) : itemsQ.error && summary.length === 0 && production.sourceType === "order" ? (
          <p role="alert" className="card-pad text-sm text-red-800">
            {itemsQ.error}
          </p>
        ) : detailItems.length === 0 ? (
          <p className="card-pad text-sm text-gray-600">
            {production.sourceType === "order"
              ? "Bu siparişte kalem kaydı yok."
              : "Bu kayıtta ürün özeti yok. Kesim ölçüleri aşağıdaki tablodadır."}
          </p>
        ) : (
          <ul role="list" className="divide-y divide-gray-200">
            {detailItems.map((it) => {
              const img = it.imageUrl || (it.productId ? productImages[it.productId] : "") || "";
              const linkable = Boolean(it.productId) && it.isProductDeleted !== true;
              return (
                <li key={it.key} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100">
                    {img ? (
                      <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-xs text-gray-600">görsel yok</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-medium text-gray-900">
                      {linkable ? (
                        <Link className="link" to={`/products/${encodeURIComponent(it.productId!)}`}>
                          {it.name}
                        </Link>
                      ) : (
                        it.name
                      )}
                      {it.detail ? <span className="text-gray-500"> · {it.detail}</span> : null}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {it.sku ? `SKU ${it.sku}` : "SKU yok"}
                      {it.isProductDeleted ? " · üretimden kalkmış ürün" : ""}
                    </p>
                  </div>
                  <span className="text-lg font-semibold tabular-nums text-gray-900">
                    {n(it.quantity, " adet")}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Kesim satırları */}
      <section className="card">
        <div className="card-pad flex flex-wrap items-center justify-between gap-2 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Kesim satırları</h2>
          <Link
            className="btn-secondary noprint"
            to={`/cutting-list?status=${encodeURIComponent(production.status ?? "")}`}
          >
            Kesim listesine git
          </Link>
        </div>

        {cuttingQ.loading ? (
          <LoadingBlock label="Kesim satırları yükleniyor" />
        ) : cuttingQ.error ? (
          <p role="alert" className="card-pad text-sm text-red-800">
            {cuttingQ.error}
          </p>
        ) : cuttingQ.docs.length === 0 ? (
          <p className="card-pad text-sm text-gray-600">
            Bu üretim için kesim ölçüsü girilmemiş.
          </p>
        ) : (
          <DataTable<CuttingLineDoc & { id: string }>
            caption="Bu üretim kaydına girilmiş kesim ölçüleri"
            rows={cuttingQ.docs}
            rowKey={(r) => r.id}
            columns={[
              {
                key: "productName",
                header: "Ürün",
                render: (r) => cuttingLineProductName(r) || cuttingLineItemId(r) || "—",
              },
              {
                key: "size",
                header: "Ölçü",
                numeric: true,
                render: (r) => `${n(r.size1)} x ${n(r.size2)}`,
              },
              { key: "quantity", header: "Adet", numeric: true, render: (r) => n(r.quantity) },
              {
                key: "thickness",
                header: "Kalınlık",
                numeric: true,
                render: (r) => (r.thickness ? `${r.thickness} mm` : "—"),
              },
              {
                key: "origin",
                header: "Malzeme",
                render: (r) => ORIGIN_LABEL[r.originOfMaterial ?? ""] ?? "—",
              },
            ]}
          />
        )}
      </section>

      <p className="text-xs text-gray-500">
        Kayıt: {productionId} · Oluşturulma: {date(created)}
      </p>

      <ShortageDialog
        state={shortage}
        canForce={isStaff}
        busy={busy}
        onClose={() => setShortage(null)}
        onForce={(reason) => {
          setShortage(null);
          void runStatusChange(true, reason);
        }}
      />
    </div>
  );
}

export default ProductionDetail;
